import React from "react";

import _ from "lodash";

export type ClassNameExtenderType = {
    [key: string]: {
        [id: string]: {
            id: string;
            className: string;
            priority: number;
        };
    };
};

export type ClassNames = {
    key: string;
    id: string;
    className: string;
    priority: number;
};

export const ClassNameExtenderContext = React.createContext<ClassNameExtenderType>({});

export const ClassNameExtenderActionsContext = React.createContext({
    addClassName: (key: string, id: string, className: string, priority: number) => {
        // empty function
    },
    addClassNames: (classNames: Array<ClassNames>) => {
        // empty function
    },
    removeClassName: (key: string, id: string) => {
        // empty function
    },
    removeClassNames: (classNames: Array<{ key: string; id: string }>) => {
        // empty function
    },
});

export const useClassNameExtenderContext = () => React.useContext(ClassNameExtenderContext);

export const useClassNameExtenderActionsContext = () => React.useContext(ClassNameExtenderActionsContext);

type PropsType = {
    children: React.ReactNode;
};

export const ClassNameExtenderContextProvider = ({ children }: PropsType) => {
    const [classNames, setClassNames] = React.useState<ClassNameExtenderType>({});

    const addClassName = React.useCallback(
        (key: string, id: string, className: string, priority: number) => {
            setClassNames((prev) => {
                const newClassNames = _.cloneDeep(prev);

                if (!(key in newClassNames)) {
                    newClassNames[key] = {};
                }

                newClassNames[key][id] = {
                    id,
                    className,
                    priority,
                };

                return newClassNames;
            });
        },
        []
    );

    const addClassNames = React.useCallback((classNames: Array<ClassNames>) => {
        setClassNames((prev) => {
            const newClassNames = _.cloneDeep(prev);

            classNames.forEach((className) => {
                if (!(className.key in newClassNames)) {
                    newClassNames[className.key] = {};
                }

                newClassNames[className.key][className.id] = {
                    id: className.id,
                    className: className.className,
                    priority: className.priority,
                };
            });

            return newClassNames;
        });
    }, []);

    const removeClassName = React.useCallback((key: string, id: string) => {
        setClassNames((prev) => {
            const newClassNames = _.cloneDeep(prev);

            if (key in newClassNames) {
                delete newClassNames[key][id];
            }

            return newClassNames;
        });
    }, []);

    const removeClassNames = React.useCallback((classNames: Array<{ key: string; id: string }>) => {
        setClassNames((prev) => {
            const newClassNames = _.cloneDeep(prev);

            classNames.forEach((className) => {
                if (className.key in newClassNames) {
                    delete newClassNames[className.key][className.id];
                }
            });

            return newClassNames;
        });
    }, []);

    const classNameExtenderActionsValue = React.useMemo(
        () => ({ addClassName, addClassNames, removeClassName, removeClassNames }),
        [addClassName, addClassNames, removeClassName, removeClassNames]
    );

    return (
        <ClassNameExtenderContext.Provider value={classNames}>
            <ClassNameExtenderActionsContext.Provider value={classNameExtenderActionsValue}>
                {children}
            </ClassNameExtenderActionsContext.Provider>
        </ClassNameExtenderContext.Provider>
    );
};
